import { useEffect, useState } from 'react';

/**
 * True only once `active` has been true continuously for `delayMs`.
 *
 * This is the loading-indicator rule: below about 200ms a spinner or skeleton
 * reads as a flicker rather than as feedback, and makes the app feel *less*
 * responsive than showing nothing at all. Most requests against a local
 * Postgres settle well inside that, so without this every navigation would
 * strobe.
 *
 * Going false is immediate — once the data has arrived, hiding the placeholder
 * should never be delayed.
 *
 * ## Shape
 *
 * The effect only ever schedules the *true* transition; the false case is
 * derived in the return expression, because "not loading means not showing a
 * placeholder" is a pure function of the input and does not need state.
 * Writing it as `setShown(false)` inside the effect is what the React Compiler
 * lint rejects, and correctly so.
 *
 * The remaining `setState` lives inside a `setTimeout` callback, which is a
 * genuine external event — there is no way to compute "a quarter of a second
 * has passed" during render.
 */
export function useDelayedFlag(active: boolean, delayMs = 250): boolean {
  const [elapsed, setElapsed] = useState(false);

  useEffect(() => {
    if (!active) return;

    const timer = setTimeout(() => setElapsed(true), delayMs);

    return () => {
      clearTimeout(timer);
      // Reset on the way out, so a later activation starts its delay afresh
      // rather than showing the placeholder instantly. This runs in response
      // to `active` flipping — an event, not a render-time derivation.
      setElapsed(false);
    };
  }, [active, delayMs]);

  return active && elapsed;
}
